/**
 * SchemaForm — hand-rolled form renderer over a (flat-ish) JSON Schema.
 *
 * Built for typed policy metadata: renders labeled fields from a Pydantic
 * model_json_schema(), supporting strings, numbers, booleans, enums/consts,
 * string arrays, and ONE level of nested object (rendered as a subsection).
 * Callers should check isSchemaSupported() first and fall back to a raw
 * JSON editor when it returns false.
 *
 * Edits are spread-merges over the incoming value — keys not described by
 * the schema (legacy back-links, telemetry, unknown forward-compatible
 * fields) ride along untouched. Clearing an optional field deletes the key
 * rather than writing ''/null.
 */

import { useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

type JsonSchemaNode = Record<string, any>;

interface ResolvedNode {
  node: JsonSchemaNode;
  nullable: boolean;
}

/** Resolve $ref / Optional-anyOf / single-allOf down to a concrete node. */
function resolveNode(node: JsonSchemaNode | undefined, root: JsonSchemaNode): ResolvedNode {
  if (!node) return { node: {}, nullable: false };
  if (node.$ref) {
    const name = String(node.$ref).split('/').pop() ?? '';
    const target = root.$defs?.[name];
    if (target) {
      const resolved = resolveNode(target, root);
      // Keep the outer description if the ref site carries one.
      return {
        node: { ...resolved.node, description: node.description ?? resolved.node.description },
        nullable: resolved.nullable,
      };
    }
    return { node: {}, nullable: false };
  }
  if (Array.isArray(node.anyOf)) {
    const nonNull = node.anyOf.filter((n: JsonSchemaNode) => n.type !== 'null');
    if (nonNull.length === 1) {
      const resolved = resolveNode(nonNull[0], root);
      return {
        node: { ...resolved.node, description: node.description ?? resolved.node.description },
        nullable: node.anyOf.length > nonNull.length || resolved.nullable,
      };
    }
    return { node, nullable: false };
  }
  if (Array.isArray(node.allOf) && node.allOf.length === 1) {
    const resolved = resolveNode(node.allOf[0], root);
    return {
      node: { ...resolved.node, description: node.description ?? resolved.node.description },
      nullable: resolved.nullable,
    };
  }
  return { node, nullable: false };
}

function isScalarKind(node: JsonSchemaNode): boolean {
  if (node.enum || node.const !== undefined) return true;
  if (node.type === 'string' || node.type === 'number' || node.type === 'integer' || node.type === 'boolean') {
    return true;
  }
  if (node.type === 'array') {
    return node.items?.type === 'string' || !!node.items?.enum;
  }
  return false;
}

function isSupportedObject(node: JsonSchemaNode, root: JsonSchemaNode, depth: number): boolean {
  if (node.type !== 'object' || !node.properties) return false;
  return Object.values(node.properties as Record<string, JsonSchemaNode>).every(raw => {
    const { node: child } = resolveNode(raw, root);
    if (isScalarKind(child)) return true;
    if (depth === 0 && child.type === 'object' && child.properties) {
      return isSupportedObject(child, root, 1);
    }
    return false;
  });
}

/** Whether SchemaForm can render every field of this root schema. */
export function isSchemaSupported(schema: JsonSchemaNode | undefined | null): boolean {
  if (!schema) return false;
  const { node } = resolveNode(schema, schema);
  return isSupportedObject(node, schema, 0);
}

interface SchemaFormProps {
  schema: JsonSchemaNode;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}

export function SchemaForm({ schema, value, onChange, disabled }: SchemaFormProps) {
  const { node: rootNode } = resolveNode(schema, schema);
  const properties: Record<string, JsonSchemaNode> = rootNode.properties ?? {};
  const required = new Set<string>(rootNode.required ?? []);

  // Seed required fixed-value fields (const / one-value enum, e.g. a
  // discriminator like applies_to) into the object so submits carry them
  // without the user having to know they exist.
  useEffect(() => {
    const seeds: Record<string, unknown> = {};
    for (const [key, raw] of Object.entries(properties)) {
      if (!required.has(key) || value[key] !== undefined) continue;
      const { node } = resolveNode(raw, schema);
      const fixed = node.const !== undefined ? node.const : node.enum?.length === 1 ? node.enum[0] : undefined;
      if (fixed !== undefined) seeds[key] = fixed;
    }
    if (Object.keys(seeds).length > 0) {
      onChange({ ...value, ...seeds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, value]);

  const setKey = (key: string, fieldValue: unknown, clear: boolean) => {
    const next: Record<string, unknown> = { ...value };
    if (clear) {
      delete next[key];
    } else {
      next[key] = fieldValue;
    }
    onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Object.entries(properties).map(([key, raw]) => {
        const { node } = resolveNode(raw, schema);

        if (node.type === 'object' && node.properties) {
          const subValue = (value[key] as Record<string, unknown>) ?? {};
          return (
            <Box
              key={key}
              sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                {node.title ?? key}
              </Typography>
              {node.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {node.description}
                </Typography>
              )}
              <SchemaForm
                schema={{ ...node, $defs: schema.$defs }}
                value={subValue}
                onChange={next => setKey(key, next, false)}
                disabled={disabled}
              />
            </Box>
          );
        }

        return (
          <ScalarField
            key={key}
            name={key}
            node={node}
            required={required.has(key)}
            value={value[key]}
            disabled={disabled}
            onChange={(fieldValue, clear) => setKey(key, fieldValue, clear)}
          />
        );
      })}
    </Box>
  );
}

interface ScalarFieldProps {
  name: string;
  node: JsonSchemaNode;
  required: boolean;
  value: unknown;
  disabled?: boolean;
  onChange: (value: unknown, clear: boolean) => void;
}

function ScalarField({ name, node, required, value, disabled, onChange }: ScalarFieldProps) {
  const label = node.title ?? name;
  const helper = node.description ?? '';

  // const / single-value enum — fixed discriminators like applies_to.
  const constValue = node.const !== undefined ? node.const : node.enum?.length === 1 ? node.enum[0] : undefined;
  if (constValue !== undefined) {
    return (
      <TextField
        label={label}
        value={String(value ?? constValue)}
        helperText={helper}
        size="small"
        fullWidth
        disabled
      />
    );
  }

  if (node.enum) {
    return (
      <TextField
        select
        label={label}
        value={(value as string) ?? ''}
        onChange={e => onChange(e.target.value, e.target.value === '')}
        helperText={helper}
        size="small"
        fullWidth
        required={required}
        disabled={disabled}
      >
        {!required && (
          <MenuItem value="">
            <em>(not set)</em>
          </MenuItem>
        )}
        {node.enum.map((option: unknown) => (
          <MenuItem key={String(option)} value={String(option)}>
            {String(option)}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (node.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value)}
            onChange={e => onChange(e.target.checked, false)}
            disabled={disabled}
          />
        }
        label={
          <Box>
            <Typography variant="body2">{label}</Typography>
            {helper && (
              <Typography variant="caption" color="text.secondary">
                {helper}
              </Typography>
            )}
          </Box>
        }
      />
    );
  }

  if (node.type === 'number' || node.type === 'integer') {
    return (
      <TextField
        label={label}
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        onChange={e => {
          const text = e.target.value;
          if (text === '') {
            onChange(undefined, true);
            return;
          }
          const parsed = node.type === 'integer' ? parseInt(text, 10) : parseFloat(text);
          if (!Number.isNaN(parsed)) onChange(parsed, false);
        }}
        helperText={helper}
        size="small"
        fullWidth
        required={required}
        disabled={disabled}
      />
    );
  }

  if (node.type === 'array') {
    const items = Array.isArray(value) ? (value as unknown[]).map(String) : [];
    const options: string[] = node.items?.enum ? node.items.enum.map(String) : [];
    return (
      <Autocomplete
        multiple
        freeSolo={!node.items?.enum}
        options={options}
        value={items}
        onChange={(_, next) => onChange(next, next.length === 0 && !required)}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            helperText={helper}
            size="small"
            required={required && items.length === 0}
          />
        )}
        disabled={disabled}
      />
    );
  }

  // Default: string.
  return (
    <TextField
      label={label}
      value={(value as string) ?? ''}
      onChange={e => {
        const text = e.target.value;
        onChange(text, text === '' && !required);
      }}
      helperText={helper}
      size="small"
      fullWidth
      required={required}
      disabled={disabled}
    />
  );
}
